<script setup>
import { ref, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useTrack } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';

import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import { CONVERSATION_PRIORITY } from 'shared/constants/messages';

import Button from 'dashboard/components-next/button/Button.vue';
import PriorityLogModal from 'dashboard/components/widgets/modal/PriorityLogModal.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const { currentAccount } = useAccount();

const isLoading = ref(false);
const showLogModal = ref(false);
const pendingPriorityItem = ref(null);

const [showPriorityDropdown, toggleDropdown] = useToggle();
const closeDropdown = () => toggleDropdown(false);
const openDropdown = () => toggleDropdown(true);

const currentChat = computed(() => getters.getSelectedChat.value);

// Check if priority log is enabled for a specific priority
const shouldShowLogModal = (priorityKey) => {
  const settings = currentAccount.value?.settings || {};
  
  // Master toggle must be enabled
  if (!settings.priority_log_enabled && settings.priority_log_enabled !== 'true') {
    return false;
  }
  
  // Check individual priority setting
  const key = priorityKey || 'none';
  const settingKey = `priority_log_${key}`;
  const settingValue = settings[settingKey];
  
  // Default to true if not explicitly set to false
  return settingValue !== false && settingValue !== 'false';
};

const priorityOptions = [
  {
    id: null,
    name: t('CONVERSATION.PRIORITY.OPTIONS.NONE'),
    icon: 'priority-none',
  },
  {
    id: CONVERSATION_PRIORITY.URGENT,
    name: t('CONVERSATION.PRIORITY.OPTIONS.URGENT'),
    icon: `priority-${CONVERSATION_PRIORITY.URGENT}`,
  },
  {
    id: CONVERSATION_PRIORITY.HIGH,
    name: t('CONVERSATION.PRIORITY.OPTIONS.HIGH'),
    icon: `priority-${CONVERSATION_PRIORITY.HIGH}`,
  },
  {
    id: CONVERSATION_PRIORITY.MEDIUM,
    name: t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM'),
    icon: `priority-${CONVERSATION_PRIORITY.MEDIUM}`,
  },
  {
    id: CONVERSATION_PRIORITY.LOW,
    name: t('CONVERSATION.PRIORITY.OPTIONS.LOW'),
    icon: `priority-${CONVERSATION_PRIORITY.LOW}`,
  },
];

const currentPriority = computed(() => {
  const priority = currentChat.value.priority;
  const option = priorityOptions.find(opt => opt.id === priority);
  return option || priorityOptions[0]; // Default to "None"
});

const handlePriorityClick = (priorityItem) => {
  closeDropdown();
  
  // Check if we should show the log modal
  if (shouldShowLogModal(priorityItem.id)) {
    pendingPriorityItem.value = priorityItem;
    showLogModal.value = true;
  } else {
    // Directly set priority without modal
    executePriorityChange(priorityItem.id);
  }
};

const executePriorityChange = (priority) => {
  isLoading.value = true;
  
  const conversationId = currentChat.value.id;
  const oldValue = currentChat.value?.priority;
  const priorityItem = priorityOptions.find(opt => opt.id === priority) || priorityOptions[0];

  store.dispatch('setCurrentChatPriority', {
    priority,
    conversationId,
  });
  
  store
    .dispatch('assignPriority', { conversationId, priority })
    .then(() => {
      useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
        oldValue,
        newValue: priority,
        from: 'Conversation Header',
      });
      useAlert(
        t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
          priority: priorityItem.name,
          conversationId,
        })
      );
      isLoading.value = false;
    })
    .catch(() => {
      isLoading.value = false;
    });
};

const handleModalConfirm = (priority) => {
  executePriorityChange(priority);
};

const handleModalClose = () => {
  showLogModal.value = false;
  pendingPriorityItem.value = null;
};
</script>

<template>
  <div class="relative flex items-center justify-end priority-actions">
    <div
      class="rounded-lg shadow outline-1 outline flex-shrink-0 outline-n-container"
    >
      <Button
        size="sm"
        color="slate"
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0 !gap-2"
        :is-loading="isLoading"
        @click="openDropdown"
      >
        <fluent-icon
          :icon="currentPriority.icon"
          size="16"
          class="flex-shrink-0"
        />
        <span>{{ currentPriority.name }}</span>
      </Button>
      <Button
        icon="i-lucide-chevron-down"
        :disabled="isLoading"
        size="sm"
        class="ltr:rounded-l-none rtl:rounded-r-none !outline-0"
        color="slate"
        trailing-icon
        @click="openDropdown"
      />
    </div>
    <div
      v-if="showPriorityDropdown"
      v-on-clickaway="closeDropdown"
      class="border rounded-lg shadow-lg border-n-strong dark:border-n-strong box-content p-2 w-fit z-10 bg-n-alpha-3 backdrop-blur-[100px] absolute block left-auto top-full mt-0.5 start-0 xl:start-auto xl:end-0 max-w-[12.5rem] min-w-[9.75rem] [&_ul>li]:mb-0"
    >
      <WootDropdownMenu class="mb-0">
        <WootDropdownItem
          v-for="option in priorityOptions"
          :key="option.id || 'none'"
        >
          <Button
            ghost
            slate
            sm
            start
            class="w-full !gap-2"
            :class="{ 'bg-n-alpha-2': currentPriority.id === option.id }"
            @click="() => handlePriorityClick(option)"
          >
            <fluent-icon
              :icon="option.icon"
              size="16"
              class="flex-shrink-0"
            />
            <span>{{ option.name }}</span>
          </Button>
        </WootDropdownItem>
      </WootDropdownMenu>
    </div>

    <!-- Priority Log Modal -->
    <PriorityLogModal
      v-if="pendingPriorityItem"
      :show="showLogModal"
      :conversation-id="currentChat.id"
      :priority-name="pendingPriorityItem.name"
      :priority-key="pendingPriorityItem.id"
      @close="handleModalClose"
      @confirm="handleModalConfirm"
    />
  </div>
</template>
